"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { PaymentMethodSelector } from "@/components/payment-method-selector";
import SaveMethodSurface from "@/components/payments/save-method-surface";
import { useRainforestPayinSessionContext } from "@/components/rainforest/rainforest-payin-session-context";
import { formatCurrency } from "@/lib/format-data";
import { PaymentMethodType } from "@/types/enums/payment";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";
import { listPaymentMethods } from "@/api/patient/portal-billing";
import { portalBillingQueryKeys } from "@/api/patient/portal-billing-query";
import { tenantQueryOptions } from "@/query/tenant";

/** A card-capture session, minus the saved methods the picker renders itself. */
type CardCaptureSession = Omit<RainforestPaymentMethodConfig, "paymentMethods">;
interface PaymentMethodPickerProps {
  patientId: string | null | undefined;
  amountCents: number;
  /** Some payment surfaces (e.g. package purchases) need card-only settlement. */
  allowAch?: boolean;
  /** Require a reusable card, excluding ACH and wallet methods. */
  cardOnly?: boolean;
  /** Disable saved-method payment until the displayed amount is final. */
  isAmountLoading?: boolean;
  /** Surface-specific saved-method charge; false keeps the checkout pending. */
  payWithSavedMethod: (paymentMethodId: string) => Promise<boolean | void>;
  /** Fired when either the iframe completes or the saved-method charge succeeds. */
  onComplete: () => void;
  /** Lazily create the hosted session only when the new-card UI is needed. */
  onNewCardSessionRequested?: () => void;
  /** Store a new card instead of opening a zero-dollar payin. */
  saveCardOnly?: boolean;
  /**
   * `paymentMethods` is omitted: the picker renders saved methods itself and
   * passes `[]` to the card form, so a caller whose endpoint does not return
   * them (the shop's, the portal's) still satisfies this.
   */
  createPaymentMethodConfig?: () => Promise<CardCaptureSession | null>;
  savePaymentMethod?: (rainforestPaymentMethodId: string, paymentMethodConfigId: string) => Promise<string>;
  paymentMethodConfigKey?: string;
}
function SaveCardSession({
  onSaved,
  createConfig,
  saveMethod,
  configKey,
  tenant
}: {
  onSaved: (paymentMethodId: string) => Promise<void>;
  createConfig: () => Promise<CardCaptureSession | null>;
  saveMethod: (rainforestPaymentMethodId: string, paymentMethodConfigId: string) => Promise<string>;
  configKey: string;
  tenant: string | undefined;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const configQueryKey = ["patient-payment-method-config", tenant, configKey] as const;
  const {
    data: config,
    isLoading,
    isError,
    refetch: refreshConfig
  } = useQuery({
    queryKey: configQueryKey,
    queryFn: createConfig,
    staleTime: 0,
    refetchOnMount: "always"
  });
  if (isLoading) {
    return <p className="text-caption text-foreground-secondary">
        Loading secure card form…
      </p>;
  }
  if (isError || !config) {
    return <p role="alert" className="text-caption text-destructive">
        We couldn&apos;t load the secure card form. Please try again.
      </p>;
  }
  return <div className="motion-preset-slide-right-sm mx-auto w-full">
      {saveError && <p role="alert" className="mb-3 text-caption text-destructive">
          We couldn&apos;t save this card. Please try again.
        </p>}
      <SaveMethodSurface paymentMethodConfig={{
      ...config,
      paymentMethods: []
    }} allowedMethods="CARD" hideButton={false} onValid={() => {}} onInvalid={() => {}} onMethodUpdated={() => {}} onFailure={error => console.error("Failed to save payment method", error)} onSuccess={async processorPaymentMethodId => {
      if (isSaving) return;
      setIsSaving(true);
      setSaveError(false);
      try {
        await onSaved(await saveMethod(processorPaymentMethodId, config.paymentMethodConfigId));
      } catch {
        setSaveError(true);
      } finally {
        setIsSaving(false);
        void refreshConfig();
      }
    }} />
    </div>;
}

/**
 * Patient-authed payment surface: lets a patient pay with a saved card or add
 * a new one. Must render inside `RainforestPayinSessionProvider`.
 */
export function PaymentMethodPicker({
  patientId,
  amountCents,
  allowAch = true,
  cardOnly = false,
  isAmountLoading = false,
  payWithSavedMethod,
  onComplete,
  onNewCardSessionRequested,
  saveCardOnly = false,
  createPaymentMethodConfig,
  savePaymentMethod,
  paymentMethodConfigKey = ""
}: PaymentMethodPickerProps) {
  const tenant = useParams<{
    tenant?: string;
  }>()?.tenant;
  const {
    data: savedMethods,
    isFetched: savedMethodsLoaded
  } = useQuery({
    queryKey: portalBillingQueryKeys.paymentMethods(tenant ?? "", patientId ?? ""),
    queryFn: listPaymentMethods,
    enabled: !!patientId,
    select: data => data ?? []
  });
  const {
    data: tenantPolicy
  } = useQuery({
    ...tenantQueryOptions.public(tenant),
    select: data_0 => ({
      allowApplePay: data_0?.patientCheckoutAllowApplePay !== false,
      allowAch: data_0?.allowAch !== false
    })
  });
  const [isPaying, setIsPaying] = useState(false);
  const {
    payinResponse
  } = useRainforestPayinSessionContext();
  const completedResponseRef = useRef<unknown>(null);
  useEffect(() => {
    if (!payinResponse || !("completed" in payinResponse) || !payinResponse.completed || completedResponseRef.current === payinResponse) {
      return;
    }
    completedResponseRef.current = payinResponse;
    onComplete();
  }, [onComplete, payinResponse]);
  const effectiveAllowAch = !cardOnly && allowAch && (tenantPolicy?.allowAch ?? true);
  const selectableSavedMethods = cardOnly ? (savedMethods ?? []).filter(method => method.type === PaymentMethodType.CARD) : effectiveAllowAch ? savedMethods ?? [] : (savedMethods ?? []).filter(method_0 => method_0.type !== PaymentMethodType.ACH);
  useEffect(() => {
    if (savedMethodsLoaded && selectableSavedMethods.length === 0) {
      onNewCardSessionRequested?.();
    }
  }, [onNewCardSessionRequested, savedMethodsLoaded, selectableSavedMethods.length]);
  const handlePay = async (paymentMethodId: string) => {
    setIsPaying(true);
    try {
      const completed = await payWithSavedMethod(paymentMethodId);
      if (completed !== false) onComplete();
    } finally {
      setIsPaying(false);
    }
  };
  const newCardSession = saveCardOnly ? createPaymentMethodConfig && savePaymentMethod ? <SaveCardSession onSaved={handlePay} createConfig={createPaymentMethodConfig} saveMethod={savePaymentMethod} configKey={paymentMethodConfigKey} tenant={tenant} /> : <p className="text-caption text-status-error-text">
        Unable to load card storage.
      </p> : <PaymentMethodSelector.NewCardSession onSuccess={onComplete} onFailure={err => console.error("Payment failed", err)} allowApplePay={!cardOnly && (tenantPolicy?.allowApplePay ?? true)} allowAch={effectiveAllowAch} className="motion-preset-slide-right-sm mx-auto w-full" />;
  if (!selectableSavedMethods.length) return newCardSession;
  const defaultSavedMethodId = selectableSavedMethods.find(method_1 => method_1.isDefault)?.id ?? selectableSavedMethods[0]?.id ?? null;
  return <PaymentMethodSelector.Provider defaultSelectedSavedMethodId={defaultSavedMethodId}>
      <PaymentMethodSelector.WhenSelectingSaved>
        <div className="space-y-3">
          <p className="text-body font-medium text-foreground">
            Pay with your saved cards
          </p>
          <PaymentMethodSelector.SavedCards methods={selectableSavedMethods} />
          <PaymentMethodSelector.PayWithSavedButton isLoading={isPaying} disabled={isAmountLoading} onPay={handlePay}>
            {isAmountLoading ? "Calculating total…" : saveCardOnly ? "Use this card" : `Pay ${formatCurrency(amountCents)}`}
          </PaymentMethodSelector.PayWithSavedButton>
          <PaymentMethodSelector.AddNewCardButton onClick={onNewCardSessionRequested} />
        </div>
      </PaymentMethodSelector.WhenSelectingSaved>
      <PaymentMethodSelector.WhenAddingNew>
        <PaymentMethodSelector.BackToSavedCardsButton />
        {newCardSession}
      </PaymentMethodSelector.WhenAddingNew>
    </PaymentMethodSelector.Provider>;
}